import IconButton from "components/blocks/IconButton";
import GridView from "components/GridView";
import { GridSchema, GridViewHeaderProps } from "components/GridView/GridView.types";
import { useCookieContext } from "contexts";
import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import useGridData from "hooks/useGridData";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { JudgeRewardResult } from "services/judgesRewardsService/models/judgesRewardsTypes";
import {RewardResponse} from "../../../services/RewardsService/models";
import {getUserSession} from "../../../helpers/UserInfo";


type RewardConfirmationModalProps = {
    isOpen: boolean;
    data? : RewardResponse,
    name: string;
};

const RefereeRewards = () => {
    const { t } = useTranslation("Judges");
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumb();
    const { toSystemThemeDateFormat } = useSystemDateTheme();
    const { isAr } = useCookieContext();

    const [rewardConfirmationModalState, setRewardConfirmationModalState] = useState<RewardConfirmationModalProps>({
        isOpen: false,
        name: "",
    });

    const { loading, gridData, totalCount,fetchGridData, exportToExcel } = useGridData<"rewardsService", JudgeRewardResult>({
        service: "rewardsService",
        endpoint: "getAllRefereesRewards",
        exportExcelEndpoint: "exportRefereesRewardsToExcel",
        exportFileName: "refereeRewards",
        localizationNameSpace: "Judges",
        defaultFilters:[
            { field: "RefereeId", value: getUserSession()?.id, operator: "equal" }
        ]
    });

    useEffect(() => {
        setBreadcrumbs([
            {
                localizationKey: "judgesRewards",
                path: "/home/RefereeRewards",
                menuItemId: "judgesRewards",
            },
        ]);
    }, []);

    const gridSchema = useMemo<GridSchema[]>(
        () => [
            // {
            //     field: "id",
            //     displayName: t("judgeName"),
            //     showOnMobile: true,
            //     sortable: true,
            //     filterable: true,
            //     render: (row: RewardResponse) => {
            //         return isAr ? row.user?.arabicName ??"" : row.user?.englishName ??"";
            //     },
            // },
            {
                field: "CourseOrder.SerialNumber",
                displayName: t("orderNumber"),
                sortable: true,
                filterable: true,
                render: (row: RewardResponse) => {
                    return (
                        <>
                            {row.courseOrder.serialNumber}
                        </>
                    );
                },
            },
            {
                field: "researchCenter",
                displayName: t("researchCenter"),
                render: (row: RewardResponse) => {
                    return  row?.courseOrder?.centreManagementDetail?.displayName;
                },
            },
            {
                field: "CourseOrder.AppliedUserId",
                filterable: false,
                sortable: true,
                filterOptions: {
                    type: "serverSideSelect",
                    serverSideSelectProps: {
                        label: t("applicantName"),
                        service: "accountService",
                        endpoint: "getUsersDropdown",
                        idValueKey: "id",
                        isPaginated: true,
                        textValueKey: isAr ? "arabicName" : "englishName",
                        useReactSelect: true,
                    }
                },
                displayName: t("applicantName"),
                render: (row: RewardResponse) => {
                    return isAr ? row.courseOrder?.appliedUser?.arabicName ??"" : row.courseOrder?.appliedUser?.englishName ?? "";
                },
            },
            {
                field: "ApprovalDate",
                filterable: true,
                sortable: true,
                filterOptions:{
                    type: "date"
                },
                displayName: t("rewardApprovalDate"),
                render: (row: RewardResponse) => {
                    return toSystemThemeDateFormat(row.approvalDate);
                },
            },
            {
                field: "Value",
                filterable: true,
                sortable: true,
                displayName: t("rewardAmount"),
                render: (row: RewardResponse) => {
                    return `${row.value} ${t("Common:riyal")}`;
                },
            },
            {
                field: "RewardStatusId",
                displayName: t("Common:status"),
                showOnMobile: true,
                sortable: true,
                filterable: true,
                filterOptions: {
                    type: "serverSideSelect",
                    serverSideSelectProps: {
                        label: t("Common:status"),
                        service: "lookupService",
                        endpoint: "GetAllRewardStatusDropDown",
                        idValueKey:"id",
                        textValueKey:"details.0.displayName",
                    }
                },
                render(row: RewardResponse) {
                    return (
                        <span className={row?.rewardStatus?.styleCode}>
              {row?.rewardStatus?.details?.[0]?.displayName ?? ""}
            </span>
                    );
                },
            },
            {
                field: "id",
                displayName: t("Common:actions"),
                showOnMobile: true,
                render: (row: RewardResponse) => {
                    return (
                        <div  className={"d-flex gap-2"}>
                     

                            {row?.fileId &&  <IconButton
                                title={t("paymentReceipt")}
                                icon="icon-receipt"
                                fitToIconWidth
                                innerIconColor="dark"
                                size="md"
                                onClick={() => {
                                    const downloadUrl = `/api/FileManager/DownloadFile/${row.fileId}`;
                                    let anchor = document.createElement("a");
                                    anchor.href = downloadUrl;
                                    anchor.target = "_blank";
                                    anchor.download = row.file?.name?? t("paymentReceipt")
                                    anchor.click();

                                }}
                            />}
                        </div>
                    );
                },
            },
        ],
        [t, toSystemThemeDateFormat],
    );

    const viewHeader: GridViewHeaderProps = {
        title: t("judgesRewards"),
        singularName: t("Common:result"),
        totalCount: totalCount,
        onExportClick: exportToExcel
    };


    
    return (
        <>
            <GridView
                loading={loading}
                gridProps={{
                    data: gridData ?? [],
                    gridSchema,
                }}
                viewHeaderProps={viewHeader}
            />

        
        </>
    );
};

export default RefereeRewards;
